import { Box, styled } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import UserGridSkeleton from '../StyledComponents/StyledSkeleton';
import { FC } from 'react';
import StyledDataGrid from '../StyledComponents/StyledDataGrid';
import SkeletonPagination from '../StyledComponents/LoadingPagination';

interface VoucherSkeletonProps {
  columns: GridColDef[];
}
const SkeletonCellBox = styled(Box)<{
  align: 'center' | 'flex-end' | 'flex-start';
}>(
  {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  ({ align }) => ({
    justifyContent: align,
  })
);

const SkeletonContainer = styled(Box)({
  height: 'calc(100vh - 200px)',
  backgroundColor: 'var(--bg-color)',
  maxHeight: '550px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const SkeletonGridWrapper = styled(Box)({
  flex: 1,
  margin: '0 auto',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  width: '100%',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
});

const SkeletonPaginationWrapper = styled(Box)({
  flexShrink: 0,
  width: '100%',
});
const VoucherSkeleton: FC<VoucherSkeletonProps> = ({ columns }) => {
  const skeletonRows = Array.from({ length: 12 }, (_, index) => ({
    id: index + 1,
    ...Object.fromEntries(columns.map((col) => [col.field, ''])),
  }));

  const renderSkeletonCell = (params: GridCellParams) => {
    let width = '50%';
    let height = '20%';
    let borderRadius = '5px';

    // Specific styles for voucher grid columns
    if (params.field === 'number') {
      width = '30%';
    } else if (params.field === 'createdAt') {
      width = '65%';
    } else if (params.field === 'payee') {
      const randomWidth = Math.floor(Math.random() * 31) + 50; // 50-80%
      width = `${randomWidth}%`;
    } else if (params.field === 'voucherType') {
      const randomWidth = Math.floor(Math.random() * 21) + 60; // 60-80%
      width = `${randomWidth}%`;
    } else if (params.field === 'billNumber') {
      width = '60%';
    } else if (params.field === 'status') {
      const randomWidth = Math.floor(Math.random() * 21) + 60; // 60-80%
      width = `${randomWidth}%`;
      height = '32px';
      borderRadius = '16px';
    } else if (
      params.field === 'billAmount' ||
      params.field === 'totalPaidAmount'
    ) {
      const randomWidth = Math.floor(Math.random() * 31) + 50; // 50-80%
      width = `${randomWidth}%`;
    } else if (params.field === 'action') {
      width = '24px';
      height = '24px';
      borderRadius = '50%';
    }

    const align =
      params.field === 'status' ||
      params.field === 'action' ||
      params.field === 'number'
        ? 'center'
        : params.field === 'billAmount' || params.field === 'totalPaidAmount'
        ? 'flex-end'
        : 'flex-start';

    return (
      <SkeletonCellBox align={align}>
        <UserGridSkeleton width={width} height={height} sx={{ borderRadius }} />
      </SkeletonCellBox>
    );
  };

  const skeletonColumns: GridColDef[] = columns.map((col) => ({
    ...col,
    renderCell: renderSkeletonCell,
  }));

  return (
    <SkeletonContainer>
      <SkeletonGridWrapper>
        <StyledDataGrid
          rows={skeletonRows}
          columns={skeletonColumns}
          disableColumnMenu
        />
      </SkeletonGridWrapper>
      <SkeletonPaginationWrapper>
        <SkeletonPagination />
      </SkeletonPaginationWrapper>
    </SkeletonContainer>
  );
};

export default VoucherSkeleton;
